import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object | object[];
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schema,
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://405-works.com';

  useEffect(() => {
    if (title) {
      document.title = title;
    }

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, true);
      updateMetaTag('twitter:description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (title) {
      updateMetaTag('og:title', title, true);
      updateMetaTag('twitter:title', title);
    }

    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
      updateMetaTag('twitter:image', ogImage);
    }

    const canonicalUrl = canonical || `${siteUrl}${location.pathname}`;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
    updateMetaTag('og:url', canonicalUrl, true);

    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Remove all existing JSON-LD scripts injected by this hook
    document.querySelectorAll('script[type="application/ld+json"][data-seo-hook]').forEach((el) => el.remove());

    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s) => {
        const scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        scriptTag.setAttribute('data-seo-hook', 'true');
        scriptTag.textContent = JSON.stringify(s);
        document.head.appendChild(scriptTag);
      });
    }
  }, [title, description, keywords, canonical, ogImage, schema, location.pathname, siteUrl]);
};

// ─── Schema generators ────────────────────────────────────────────────────────

export const generateLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://405-works.com';

  return {
    '@context': 'https://schema.org',
    '@type': ['CoworkingSpace', 'LocalBusiness'],
    '@id': `${siteUrl}/#business`,
    name: '405-Works',
    description:
      'Professional private offices and co-working spaces in Oklahoma City with flexible memberships and all-inclusive amenities. Starting at $300/month.',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/a4fec89bcc5ba166ddea2bf47ff2dc5f/e977f47ab61e1eb868be39c5ee14b384.webp',
    },
    image: 'https://static.readdy.ai/image/a4fec89bcc5ba166ddea2bf47ff2dc5f/19e2b86b8782be75d894180ea9a9702b.png',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '2520 W. I-44 Service Road',
      addressLocality: 'Oklahoma City',
      addressRegion: 'OK',
      postalCode: '73112',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 35.5182,
      longitude: -97.5639,
    },
    hasMap: 'https://maps.google.com/maps?q=2520+NW+39th+St,+Oklahoma+City,+OK+73112',
    priceRange: '$$',
    currenciesAccepted: 'USD',
    paymentAccepted: 'Cash, Credit Card',
    areaServed: {
      '@type': 'City',
      name: 'Oklahoma City',
    },
    amenityFeature: [
      { '@type': 'LocationFeatureSpecification', name: 'Private Offices', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Conference Rooms', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'High-Speed Internet', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Kitchen Facilities', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'On-Site Receptionist', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Cleaning Services', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Furnished Desks', value: true },
    ],
    offers: [
      {
        '@type': 'Offer',
        name: 'Private Office',
        description: 'Private offices ranging from 71sf to 202sf with conference room access included.',
        price: '300',
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '300',
          priceCurrency: 'USD',
          unitText: 'MONTH',
        },
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: '405-Works', url: siteUrl },
      },
      {
        '@type': 'Offer',
        name: 'Private Office Group',
        description: 'Spacious office suites from 536sf to 1231sf, ideal for growing teams.',
        price: '1400',
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '1400',
          priceCurrency: 'USD',
          unitText: 'MONTH',
        },
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: '405-Works', url: siteUrl },
      },
    ],
    sameAs: [siteUrl],
  };
};

export const generateHomeFAQSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Where is 405-Works located?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '405-Works is located at 2520 W. I-44 Service Road, between Penn and May Avenues in Oklahoma City, OK 73112.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does a private office at 405-Works cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Private offices at 405-Works start as low as $300/month. Private Office Groups (suites) start at $1,400/month.',
      },
    },
    {
      '@type': 'Question',
      name: 'What amenities are included with an office membership?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'All memberships include conference room access, office essentials (paper, pens, staples, etc.), a fully stocked common area with coffee and snacks, an on-site receptionist, weekly cleaning services, and a furnished desk.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are conference rooms available for non-members?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. The small conference room (seats up to 6) is available at $75/hour and the large conference room (seats up to 24) at $125/hour for non-members.',
      },
    },
  ],
});

export const generateWebPageSchema = (pageName: string, description: string, path = '') => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://405-works.com';
  const pageUrl = `${siteUrl}${path || window.location.pathname}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    name: pageName,
    description,
    url: pageUrl,
    inLanguage: 'en-US',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      name: '405-Works',
      url: siteUrl,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: pageName,
          item: pageUrl,
        },
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: '405-Works',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/a4fec89bcc5ba166ddea2bf47ff2dc5f/e977f47ab61e1eb868be39c5ee14b384.webp',
      },
    },
  };
};

export const generateOfficeMembershipServiceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://405-works.com';

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Office Membership — 405-Works',
    description:
      'Flexible private office memberships in Oklahoma City. Choose from individual private offices (from $300/month) or private office groups (from $1,400/month) with all-inclusive amenities.',
    provider: {
      '@type': 'CoworkingSpace',
      name: '405-Works',
      url: siteUrl,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2520 W. I-44 Service Road',
        addressLocality: 'Oklahoma City',
        addressRegion: 'OK',
        postalCode: '73112',
        addressCountry: 'US',
      },
    },
    areaServed: { '@type': 'City', name: 'Oklahoma City' },
    serviceType: 'Coworking Space',
    offers: [
      {
        '@type': 'Offer',
        name: 'Private Office',
        price: '300',
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '300',
          priceCurrency: 'USD',
          unitText: 'MONTH',
        },
      },
      {
        '@type': 'Offer',
        name: 'Private Office Group',
        price: '1400',
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '1400',
          priceCurrency: 'USD',
          unitText: 'MONTH',
        },
      },
    ],
  };
};
